﻿export const newsFinder = () => {
  /** Wires up accordion behavior for category filters */
  const setupFilterAccordion = () => {
    const filterSections = document.querySelectorAll("#filters section");

    const expandedSectionsElement = document.querySelector(
      "#expanded-facets"
    ) as HTMLInputElement;

    const collapsedUlClassName = "max-h-0";

    const toggleSection = (
      sectionElement: HTMLElement,
      visible?: boolean | undefined
    ) => {
      const ul = sectionElement.querySelector("ul") as HTMLElement;

      const collapsedIcon = sectionElement.querySelector(
        "svg[data-icon=collapsed]"
      ) as HTMLElement;

      const expandedIcon = sectionElement.querySelector(
        "svg[data-icon=expanded]"
      ) as HTMLElement;

      // Toggle list visibility if needed
      if (
        (visible === true && ul.classList.contains(collapsedUlClassName)) ||
        (visible === false && !ul.classList.contains(collapsedUlClassName)) ||
        visible === undefined
      ) {
        ul.classList.toggle(collapsedUlClassName);

        if (ul.classList.contains(collapsedUlClassName)) {
          expandedIcon.classList.add("hidden");
          collapsedIcon.classList.remove("hidden");
        } else {
          collapsedIcon.classList.add("hidden");
          expandedIcon.classList.remove("hidden");
        }
      }
    };

    const refreshExpandedSectionsValue = (expandSectionsWithFilters: boolean) => {
      expandedSectionsElement.value = "";

      const expandedSections: string[] = [];

      filterSections.forEach(section => {
        const sectionElement = section as HTMLElement;

        const isCollapsed = !!sectionElement.querySelector(`ul.${collapsedUlClassName}`);

        const hasCheckedFilters =
          !!sectionElement.querySelectorAll(`input[checked]`).length;

        // Also consider expanded if there is at least one checked filter
        if ((!isCollapsed || hasCheckedFilters) && sectionElement.dataset.facets) {
          expandedSections.push(sectionElement.dataset.facets);

          if (expandSectionsWithFilters) {
            toggleSection(sectionElement, true);
          }
        }
      });

      expandedSectionsElement.value = expandedSections.join(",");
    };

    filterSections.forEach(section => {
      // Show/hide filters group
      section.addEventListener("click", _ => {
        toggleSection(section as HTMLElement);

        refreshExpandedSectionsValue(false);
      });
    });

    refreshExpandedSectionsValue(true);
  };

  /** Wires up event to post form whenever values (e.g. filters) change. */
  const wireUpFormInputElements = () => {
    const form = document.getElementById("news-finder-form") as HTMLFormElement;

    form.querySelectorAll<HTMLInputElement>("label").forEach(elm => {
      elm.addEventListener("click", ev => {
        ev.stopPropagation(); // Prevent section from receiving click, as that hides the section
      });
    });

    form.querySelectorAll<HTMLInputElement>("input").forEach(elm => {
      elm.addEventListener("click", ev => {
        ev.stopPropagation(); // Prevent section from receiving click, as that hides the section
      });

      elm.addEventListener("change", _ => {
        if (elm.disabled) {
          // Ignore changes to disabled elements
          return;
        }

        refreshForm(elm.closest("form") as HTMLFormElement);
      });
    });
  };

  /** Wires up event to post form whenever selected item (e.g. sort order) changes. */
  const wireUpSelectElements = () => {
    const form = document.getElementById("news-finder-form") as HTMLFormElement;

    form.querySelectorAll<HTMLSelectElement>("select").forEach(elm => {
      elm.addEventListener("change", _ => {
        if (elm.disabled) {
          // Ignore changes to disabled elements
          return;
        }

        refreshForm(elm.closest("form") as HTMLFormElement);
      });
    });
  };

  /** Posts a form asynchronously and returns the response HTML. */
  const postForm = (form: HTMLFormElement) =>
    new Promise<string>((resolve, reject) => {
      fetch(form.action, {
        method: "POST",
        body: new FormData(form),
      })
        .then(response => response.text())
        .then(html => {
          resolve(html);
        })
        .catch(error => {
          reject(error);
        });
    });

  /**
   * Adds/sets a query string parameter value, or removes it if no value is specified.
   * @param params
   * @param name
   * @param value
   * @returns The updated set of parameters.
   */
  const setQueryString = (
    params: URLSearchParams,
    name: string,
    value: string | null
  ) => {
    if (value) {
      // We don't use encodeURIComponent primarily for aesthetic reasons,
      // instead we explicitly replace expected characters
      value = value.replaceAll("&", "%26");

      params.set(name, value);
    } else {
      params.delete(name);
    }

    return params;
  };

  /** Updates query string to match current filters */
  const updateUrl = () => {
    let params = new URLSearchParams(window.location.search);

    // Update query string to match current filters
    const newsFinderWrapper = document.getElementById(
      "news-finder-wrapper"
    ) as HTMLElement;

    const keywords = newsFinderWrapper.dataset["keywords"] || "";
    const types = newsFinderWrapper.dataset["types"] || "";
    const topics = newsFinderWrapper.dataset["topics"] || "";
    const tags = newsFinderWrapper.dataset["tags"] || "";
    const years = newsFinderWrapper.dataset["years"] || "";
    const orderby = newsFinderWrapper.dataset["sort"] || "";
    const page = newsFinderWrapper.dataset["page"] || "";

    params = setQueryString(params, "keywords", keywords);
    params = setQueryString(params, "types", types);
    params = setQueryString(params, "topics", topics);
    params = setQueryString(params, "tags", tags);
    params = setQueryString(params, "years", years);
    params = setQueryString(params, "orderby", orderby);

    if (parseInt(page) > 1) {
      params = setQueryString(params, "page", page);
    } else {
      params = setQueryString(params, "page", null);
    }

    const hasParameters = params.toString() !== "";

    const queryString = hasParameters ? `?${decodeURIComponent(params.toString())}` : "";

    const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${queryString}`;

    window.history.replaceState({}, "", newUrl);
  };

  /** Posts the current form and expects HTML to be returned for updating the page. */
  const refreshForm = (form: HTMLFormElement) => {
    postForm(form).then(
      html => {
        let container = document.getElementById("news-finder-wrapper") as HTMLElement;

        container.outerHTML = html;

        updateUrl();

        initialize();

        // Refresh container to be able to scroll to it (as it has effectively been replaced in the DOM)
        container = document.getElementById("news-finder-wrapper") as HTMLElement;

        if (container) {
          container.scrollIntoView({ behavior: "smooth" });
        }
      },
      error => {
        console.error("Failed to refresh news", error);
      }
    );
  };

  /** Sets the visibility of the filters panel (applicable for tablet and mobile). */
  const showHideFilterPanel = (show: boolean | null) => {
    const filtersContainer = document.getElementById("filters") as HTMLDivElement;

    if (show === null) {
      show = !filtersContainer.classList.contains("!translate-x-0");
    }

    const overlay = document.getElementById(
      "news-finder-wrapper-overlay"
    ) as HTMLDivElement;

    const filtersExpanded = document.getElementById(
      "filtersExpanded"
    ) as HTMLInputElement;

    // Set checkbox to indicate whether panel is open
    filtersExpanded.checked = show;

    // Toggle visibility of filters container
    if (show) {
      filtersContainer.classList.add("!translate-x-0");

      overlay.classList.remove("pointer-events-none");
      overlay.classList.add("!opacity-100");
    } else {
      filtersContainer.classList.remove("!translate-x-0");

      overlay.classList.add("pointer-events-none");
      overlay.classList.remove("!opacity-100");
    }
  };

  /** Shows/hides the filters panel (in mobile and tablet) when button is clicked. */
  const wireUpFiltersButton = () => {
    const filtersButton = document.getElementById(
      "show-hide-filters-button"
    ) as HTMLButtonElement;

    filtersButton.addEventListener("click", _ => {
      showHideFilterPanel(null);
    });
  };

  /** Resets all filters */
  const clearAllFilters = () => {
    const form = document.getElementById("news-finder-form") as HTMLFormElement;

    form
      .querySelectorAll<HTMLInputElement>("input[type=checkbox]:not(#filtersExpanded)") // Don't reset the filters expanded checkbox, as we want the panel to remain open
      .forEach(elm => {
        elm.checked = false;
      });

    form.querySelectorAll<HTMLSelectElement>("select").forEach(elm => {
      elm.value = "";
    });

    form.querySelectorAll<HTMLInputElement>("input[type=text]").forEach(elm => {
      elm.value = "";
    });

    setPagingPage(1);
  };

  /**
   * Adds a form element to render a different "paging page". Note: this element isn't included in
   * initial markup, as the default behavior is to reset to the first paging page when filters change.
   * @param page The target page number, for example 2 to load the second paging page.
   */
  const setPagingPage = (page: number) => {
    if (page < 1) {
      throw "Unsupported page number, must be a positive number";
    }

    const form = document.getElementById("news-finder-form") as HTMLFormElement;

    if (form) {
      // Create a hidden input element with the value of the target paging page
      const hiddenInput = document.createElement("input");
      hiddenInput.name;
      hiddenInput.type = "hidden";
      hiddenInput.name = "currentPageNumber";
      hiddenInput.value = page.toString();

      // Append the hidden input to the form
      form.appendChild(hiddenInput);

      // Post the form including the paging form element
      refreshForm(form);
    }
  };

  /** Configures search textbox */
  const configureSearchBox = () => {
    const searchTextboxes = document.querySelectorAll<HTMLInputElement>(
      "[data-search-type=keywords]"
    );

    searchTextboxes.forEach(element => {
      // Trigger change on keywords textbox when Enter is pressed
      element.addEventListener("keydown", event => {
        if (event.key === "Enter") {
          (event.target as HTMLInputElement).blur(); // To trigger change, if we trigger the actual 'change' event we get double posts
        }
      });

      // Reset search box value when X is clicked
      (
        element.parentElement?.querySelector(
          "svg[data-search-function=clear]"
        ) as HTMLElement
      ).addEventListener("click", event => {
        element.value = "";

        // Return to search icon
        const clearIcon = event.target as HTMLElement;

        clearIcon.classList.add("hidden");

        const searchIcon = element.parentElement?.querySelector(
          "svg:not([data-search-function=clear])"
        );

        searchIcon?.classList.remove("hidden");

        // Make the change take effect
        element.parentElement?.querySelector("input")?.dispatchEvent(new Event("change"));
      });
    });
  };

  const configureForm = () => {
    // Prevent POST of form, as we'll do it async (AJAX)
    const form = document.getElementById("news-finder-form") as HTMLFormElement;

    form.onsubmit = event => {
      event.preventDefault();
    };

    // Prevent form from being submitted by hitting Enter
    form.addEventListener("keydown", event => {
      if (event.key === "Enter") {
        event.preventDefault();
      }
    });
  };

  /** Sets up paging buttons so that they trigger an AJAX refresh rather than a full page load */
  const configurePaging = () => {
    document.querySelectorAll<HTMLElement>("a[data-page-number]").forEach(elm => {
      elm.addEventListener("click", evt => {
        const link = evt.target as HTMLAnchorElement;

        if (!link) {
          console.warn("Expected element to be an anchor");

          return;
        }

        const pageNumberString = link.dataset["pageNumber"];

        if (pageNumberString) {
          const targetPage = parseInt(pageNumberString);

          if (targetPage) {
            setPagingPage(targetPage);
          }

          evt.preventDefault();
        }
      });
    });
  };

  /** Sets up behaviors after page load/refresh. */
  const initialize = () => {
    configureSearchBox();

    wireUpFormInputElements();

    wireUpSelectElements();

    wireUpFiltersButton();

    configureForm();

    setupFilterAccordion();

    configurePaging();

    // Hide filters panel on Esc (applicable for tablet and mobile)
    document.addEventListener("keydown", event => {
      if (event.key === "Escape") {
        showHideFilterPanel(false);
      }
    });

    // Hide filters panel if clicking outside (i.e. on the overlay)
    document
      .getElementById("news-finder-wrapper-overlay")
      ?.addEventListener("click", _ => {
        showHideFilterPanel(false);
      });

    // Hide filters button if "Show news" button is clicked
    (
      document.getElementById("news-finder-filters-panel-button") as HTMLButtonElement
    ).addEventListener("click", _ => {
      showHideFilterPanel(false);
    });

    // Clear filters on click
    (
      document.getElementById("news-finder-clear-filters-button") as HTMLElement
    ).addEventListener("click", ev => {
      ev.stopPropagation();

      clearAllFilters();
    });
  };

  initialize();
};
